import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Buildings, Palette, Flask as FlaskIcon, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useBranding } from "@/contexts/BrandingContext";
import BrandLogo from "@/components/BrandLogo";

const EMPTY = {
  name: "", address: "", phone: "", email: "", siret: "", evtc_number: "", legal_mentions: "",
  legal_name: "", vat_status: "", vat_number: "", payment_terms: "", late_penalty_rate: "",
};

export default function CompanySettings() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/company");
        setForm((p) => ({ ...p, ...data }));
      } catch {}
    })();
  }, []);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/settings/company", form);
      toast.success("Profil de l'entreprise enregistré");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-settings" className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Button data-testid="settings-back-btn" variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-lg font-medium text-white flex items-center gap-2">
            <Buildings size={20} className="text-amber-500" /> Profil de l'entreprise
          </h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <form onSubmit={save} data-testid="company-form" className="space-y-4 bg-[#121212] border border-zinc-800 rounded-lg p-5">
          <div className="flex items-center gap-4 pb-3 border-b border-zinc-800">
            {/* Phase 33.2 — single source of truth: company branding (was
                a hardcoded /brand/sdrive-logo.png + misleading note). */}
            <BrandLogo className="h-14 w-auto shrink-0" />
            <div>
              <p className="text-white font-medium">Informations affichées sur les bons de transport et les communications clients.</p>
              <p className="text-xs text-zinc-500 mt-1">Logo et couleur principale modifiables ci-dessous, dans <span className="text-amber-400">Identité visuelle</span>.</p>
            </div>
          </div>

          <Row label="Nom de l'entreprise *"><Input data-testid="co-name" required value={form.name} onChange={set("name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Row>
          <Row label="Adresse"><Input data-testid="co-address" value={form.address} onChange={set("address")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Row>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Row label="Téléphone"><Input data-testid="co-phone" value={form.phone} onChange={set("phone")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Row>
            <Row label="E-mail"><Input data-testid="co-email" type="email" value={form.email} onChange={set("email")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Row>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Row label="SIRET"><Input data-testid="co-siret" value={form.siret} onChange={set("siret")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Row>
            <Row label="Numéro EVTC"><Input data-testid="co-evtc" value={form.evtc_number} onChange={set("evtc_number")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></Row>
          </div>

          {/* Phase 26 — invoicing block. Used ONLY by dispatch client invoices;
              the driver-personal invoice module has its own profile logic. */}
          <div className="pt-2 border-t border-zinc-800 space-y-3">
            <p className="text-xs uppercase tracking-wider text-amber-400">Facturation client dispatch</p>
            <Row label="Raison sociale (optionnel — apparaît sur la facture)">
              <Input data-testid="co-legal-name" value={form.legal_name} onChange={set("legal_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </Row>
            <Row label="Statut TVA">
              <select data-testid="co-vat-status" value={form.vat_status} onChange={set("vat_status")}
                className="w-full bg-[#1A1A1A] border border-zinc-700 text-white h-10 px-3 rounded-md focus:border-amber-500 focus:outline-none">
                <option value="">— Sélectionner —</option>
                <option value="non_assujetti">Non assujetti — franchise en base (art. 293 B du CGI)</option>
                <option value="assujetti">Assujetti à la TVA</option>
              </select>
            </Row>
            {form.vat_status === "assujetti" && (
              <Row label="Numéro TVA intracommunautaire">
                <Input data-testid="co-vat-number" value={form.vat_number} onChange={set("vat_number")} placeholder="FRXX999999999"
                  className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
              </Row>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Row label="Conditions de règlement">
                <Input data-testid="co-payment-terms" value={form.payment_terms} onChange={set("payment_terms")} placeholder="Ex: 30 jours à réception"
                  className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
              </Row>
              <Row label="Pénalités de retard">
                <Input data-testid="co-late-penalty" value={form.late_penalty_rate} onChange={set("late_penalty_rate")} placeholder="Ex: 3× taux légal"
                  className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
              </Row>
            </div>
          </div>
          <Row label="Mentions légales (affichées sur le bon de transport)">
            <Textarea data-testid="co-legal" rows={4} value={form.legal_mentions} onChange={set("legal_mentions")} className="bg-[#1A1A1A] border-zinc-700 text-white" />
            <p className="text-[11px] text-zinc-500 mt-1">Vous pouvez utiliser les variables <code className="text-amber-400">{'{SIRET}'}</code> et <code className="text-amber-400">{'{EVTC}'}</code>.</p>
          </Row>
          <Button data-testid="co-save" type="submit" disabled={loading} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11">
            {loading ? "..." : "Enregistrer"}
          </Button>
        </form>

        {/* ─── Phase 32.1 · Branding (logo + couleur) éditable par l'admin ─── */}
        <BrandingPanel />

        {/* ─── Phase 31.1 · Demo data buttons accessible to company_admin ─── */}
        <DemoDataPanel />
      </main>
    </div>
  );
}

/**
 * Branding editor — uploads a logo, picks a primary color. Calls the
 * tenant-scoped endpoints; no super_admin needed. Live-refreshes the global
 * BrandingContext on success so the dashboard rebrands immediately.
 */
function BrandingPanel() {
  const branding = useBranding();
  const fileRef = useRef(null);
  const [color, setColor] = useState(branding.primaryColor || "#F59E0B");
  const [companyName, setCompanyName] = useState(branding.companyName || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Keep local state in sync if context updates (e.g. after another save).
  useEffect(() => { setColor(branding.primaryColor || "#F59E0B"); }, [branding.primaryColor]);
  useEffect(() => { setCompanyName(branding.companyName || ""); }, [branding.companyName]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/company-branding", {
        primary_color: color,
        company_name:  companyName,
      });
      await branding.refresh();
      toast.success("Branding enregistré");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const onLogoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Image trop volumineuse (max 2 Mo)"); return; }
    const fd = new FormData(); fd.append("file", f);
    setUploading(true);
    try {
      await api.post("/admin/company-branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await branding.refresh();
      toast.success("Logo mis à jour");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setSaving(true);
    try {
      await api.put("/admin/company-branding", { logo_url: "" });
      await branding.refresh();
      toast.success("Logo supprimé");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec");
    } finally { setSaving(false); }
  };

  const PRESETS = [
    { name: "Amber",   value: "#F59E0B" },
    { name: "Violet",  value: "#8B5CF6" },
    { name: "Bleu",    value: "#3B82F6" },
    { name: "Émeraude",value: "#10B981" },
    { name: "Rose",    value: "#EC4899" },
    { name: "Rouge",   value: "#EF4444" },
  ];

  return (
    <section data-testid="branding-panel" className="space-y-4 bg-[#121212] border border-zinc-800 rounded-lg p-5 mt-6">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-800">
        <Palette size={20} className="text-amber-500" />
        <div>
          <h2 className="text-white font-medium">Identité visuelle</h2>
          <p className="text-xs text-zinc-500">Personnalisez le logo et la couleur principale de votre dispatch (visibles partout dans l'application).</p>
        </div>
      </div>
      <Row label="Nom de la société (affiché dans l'en-tête)">
        <Input data-testid="branding-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
               className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
      </Row>
      <Row label="Logo">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded bg-[#1A1A1A] border border-zinc-700 flex items-center justify-center overflow-hidden">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="logo" className="max-h-12 max-w-12 object-contain" />
            ) : (
              <span className="text-xs text-zinc-600">aucun</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={onLogoPick} className="hidden" data-testid="branding-logo-input" />
            <Button type="button" data-testid="branding-logo-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
                    variant="outline" size="sm" className="border-zinc-700 text-white hover:bg-zinc-800">
              {uploading ? "..." : "Choisir un fichier"}
            </Button>
            {branding.logoUrl && !branding.isDefault && (
              <Button type="button" data-testid="branding-logo-remove" onClick={removeLogo} variant="ghost" size="sm" className="text-zinc-500 hover:text-red-400">
                <Trash size={14} className="mr-1" /> Supprimer
              </Button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">PNG, JPG ou SVG · 2 Mo max</p>
      </Row>
      <Row label="Couleur principale">
        <div className="flex items-center gap-3 flex-wrap">
          <input data-testid="branding-color-input" type="color" value={color} onChange={(e) => setColor(e.target.value)}
                 className="h-10 w-14 rounded border border-zinc-700 bg-[#1A1A1A] cursor-pointer" />
          <Input data-testid="branding-color-hex" value={color} onChange={(e) => setColor(e.target.value)}
                 className="bg-[#1A1A1A] border-zinc-700 text-white h-10 w-32 font-mono uppercase" />
          <div className="flex gap-1.5 flex-wrap">
            {PRESETS.map(p => (
              <button key={p.value} type="button" data-testid={`branding-preset-${p.name.toLowerCase()}`}
                      onClick={() => setColor(p.value)} title={`${p.name} · ${p.value}`}
                      className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${color.toLowerCase() === p.value.toLowerCase() ? "border-white" : "border-zinc-700"}`}
                      style={{ backgroundColor: p.value }} />
            ))}
          </div>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">La couleur est légèrement adoucie automatiquement pour le confort visuel.</p>
      </Row>
      <Button data-testid="branding-save" onClick={save} disabled={saving}
              className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11">
        {saving ? "..." : "Enregistrer le branding"}
      </Button>
    </section>
  );
}

/**
 * Demo data panel — one-click seed / wipe of the canonical demo dataset
 * for the caller's company. Backed by /api/admin/demo-seed.
 */
function DemoDataPanel() {
  const [busy, setBusy] = useState(null);   // "seed" | "wipe" | null
  const [lastResult, setLastResult] = useState(null);

  const run = async (action) => {
    if (busy) return;
    if (action === "wipe" &&
        !window.confirm("Supprimer toutes les données de démonstration de votre société ?")) return;
    setBusy(action);
    try {
      let res;
      if (action === "seed") {
        res = await api.post("/admin/demo-seed?mode=reseed");
        const s = res.data?.summary;
        toast.success(`Démo chargée · ${s?.users_created} comptes · ${s?.rides_created} courses · ${s?.ride_requests_created} demandes`);
      } else {
        res = await api.delete("/admin/demo-seed");
        toast.success(`Démo supprimée · ${res.data?.users_deleted ?? 0} comptes · ${res.data?.rides_deleted ?? 0} courses`);
      }
      setLastResult(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec");
    } finally { setBusy(null); }
  };

  return (
    <section data-testid="demo-panel" className="space-y-3 bg-[#121212] border border-zinc-800 rounded-lg p-5 mt-6">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-800">
        <FlaskIcon size={20} className="text-amber-500" />
        <div>
          <h2 className="text-white font-medium">Données de démonstration</h2>
          <p className="text-xs text-zinc-500">Pour vos démos commerciales : générez 4 comptes (2 chauffeurs + 2 agences), 6 courses cohérentes et 2 demandes en attente. Tout est tagué <code className="text-amber-400">DEMO</code> et supprimable en un clic.</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button data-testid="demo-seed-btn" onClick={() => run("seed")} disabled={busy !== null}
                className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11 flex-1">
          {busy === "seed" ? "..." : "Charger données démo"}
        </Button>
        <Button data-testid="demo-wipe-btn" onClick={() => run("wipe")} disabled={busy !== null}
                variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-11 flex-1">
          {busy === "wipe" ? "..." : "Supprimer données démo"}
        </Button>
      </div>
      {lastResult?.demo_password && (
        <p className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-800">
          Mot de passe pour tous les comptes démo : <code className="text-amber-400 font-mono">{lastResult.demo_password}</code>
        </p>
      )}
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-zinc-400 uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
